import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Layers,
  GraduationCap,
  AlertTriangle,
  BookOpen,
  EyeOff,
  Plus,
  Trash2,
  Check,
  Eye,
  ArrowUp,
  ArrowDown,
  X,
} from 'lucide-react'
import AdminNav from '../components/AdminNav'
import { useAuth } from '../context/AuthContext'
import { useConfig } from '../context/ConfigContext'
import { modules as builtInModules, SEED_COURSES as builtInCourses } from '../data/modules'
import { mergeCatalogue, modulesInCourse, phantomCourses, isPublished, isRequired, isCoursePublished, addableModules, courseModuleIds, moduleNumberIn } from '../data/catalogue'
import { slugify, courseChapters, unknownChapters, degreesUsingCourse } from '../data/courses'

const CHAPTER_COLORS = ['blue', 'green', 'purple', 'amber', 'red', 'cyan', 'indigo', 'teal', 'rose']

const CHAPTER_BADGE = {
  amber: 'bg-amber-100 text-amber-700',
  blue: 'bg-blue-100 text-blue-700',
  green: 'bg-green-100 text-green-700',
  purple: 'bg-purple-100 text-purple-700',
  red: 'bg-red-100 text-red-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  teal: 'bg-teal-100 text-teal-700',
  rose: 'bg-rose-100 text-rose-700',
}

/**
 * Courses — the level between a degree and a module.
 *
 * Editable by admins only, matching firestore.rules. A manager sees the same
 * page without the controls: they can read what the curriculum is, they just
 * do not decide it. Showing them buttons the server refuses would be the exact
 * mistake this app already made once with the manager tier.
 *
 * CHAPTERS live here rather than on each module, which is the other half of the
 * change. A module names its chapter; the course decides what chapters exist
 * and in what order they run.
 */
export default function AdminCoursesPage() {
  const { user } = useAuth()
  const { degrees, courses, allCourses, customModules, seeded, saveCourses, loading } = useConfig()
  const canEdit = user?.isAdmin === true || user?.isManager === true

  const [draftCourse, setDraftCourse] = useState('')
  const [draftChapter, setDraftChapter] = useState({})
  const [draftModule, setDraftModule] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const knownIds = useMemo(() => builtInCourses.map((t) => t.id), [])
  const catalogue = useMemo(
    () => mergeCatalogue(builtInModules, customModules || []),
    [customModules],
  )
  const phantoms = useMemo(() => phantomCourses(catalogue, (courses || []).map((c) => c.id)), [catalogue, courses])
  const phantomIds = useMemo(() => new Set(phantoms.map((p) => p.id)), [phantoms])

  const rows = useMemo(() => (allCourses || []).map((course) => {
    const mods = modulesInCourse(catalogue, course)
    const published = mods.filter(isPublished)
    return {
      course,
      isPhantom: phantomIds.has(course.id),
      isSeedCourse: knownIds.includes(course.id),
      modules: mods,
      addable: addableModules(course, catalogue),
      published,
      required: mods.filter(isRequired),
      lessons: published.reduce((sum, m) => sum + m.lessons.length, 0),
      strayChapters: unknownChapters(course, published),
      usedBy: degreesUsingCourse(course.id, degrees),
    }
  }), [allCourses, catalogue, degrees, phantomIds, knownIds])

  async function commit(next) {
    setSaving(true)
    setError(null)
    const res = await saveCourses(next)
    setSaving(false)
    if (!res?.ok) setError('That did not save — check you still have admin or manager rights.')
  }

  function addCourse() {
    const name = draftCourse.trim()
    if (!name) return
    const id = slugify(name)
    if (!id) return setError('That name has no letters or numbers to make an id from.')
    if ((allCourses || []).some((c) => c.id === id)) return setError(`A course with the id "${id}" already exists.`)
    commit([...courses, { id, name, description: '', chapters: [] }])
    setDraftCourse('')
  }

  function updateCourse(id, patch) {
    commit(courses.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }

  /**
   * Membership is a property of the COURSE, so all three of these are ordinary
   * edits to the courses config — the same `commit` as renaming a course.
   *
   * Nothing is written to the module at all. That is what makes a built-in
   * module addable: it used to require publishing a Firestore override under
   * its id, permanently converting a repo-defined module into a data-defined
   * one, so built-ins had to be offered disabled. Appending an id to a list
   * costs the module nothing.
   *
   * `courseModuleIds` materialises the list on first edit from whatever the
   * `track` fallback currently resolves to, so editing a course that has never
   * had a list starts from exactly what it shows today rather than from empty.
   */
  function withModules(course, nextIds) {
    return commit(courses.map((c) => (c.id === course.id ? { ...c, modules: nextIds } : c)))
  }

  function addModuleToCourse(course) {
    const moduleId = draftModule[course.id]
    if (!moduleId) return
    const ids = courseModuleIds(course, catalogue)
    if (ids.includes(String(moduleId))) return
    withModules(course, [...ids, String(moduleId)])
    setDraftModule((d) => ({ ...d, [course.id]: '' }))
  }

  function removeModuleFromCourse(course, moduleId) {
    // Removal from a COURSE, not deletion. The module keeps existing and keeps
    // every other course it is in — which is only a meaningful distinction now
    // that it can be in more than one.
    withModules(course, courseModuleIds(course, catalogue).filter((id) => id !== String(moduleId)))
  }

  function moveModule(course, moduleId, delta) {
    const ids = courseModuleIds(course, catalogue)
    const from = ids.indexOf(String(moduleId))
    const to = from + delta
    if (from < 0 || to < 0 || to >= ids.length) return
    const next = [...ids]
    ;[next[from], next[to]] = [next[to], next[from]]
    withModules(course, next)
  }

  function addChapter(course) {
    const name = (draftChapter[course.id] || '').trim()
    if (!name) return
    const existing = courseChapters(course)
    if (existing.some((ch) => ch.name === name)) return setError(`"${name}" is already a chapter of ${course.name}.`)
    const color = CHAPTER_COLORS[existing.length % CHAPTER_COLORS.length]
    updateCourse(course.id, { chapters: [...existing, { name, color }] })
    setDraftChapter((d) => ({ ...d, [course.id]: '' }))
  }

  function removeChapter(course, name) {
    const orphans = modulesInCourse(catalogue, course.id).filter((m) => m.phase === name)
    const warning = orphans.length
      ? `\n\n${orphans.length} module${orphans.length === 1 ? '' : 's'} name this chapter. They stay published and keep their progress — they will appear under an unplanned heading at the end of the course until you re-file them.`
      : ''
    if (!window.confirm(`Remove the "${name}" chapter from ${course.name}?${warning}`)) return
    updateCourse(course.id, { chapters: courseChapters(course).filter((ch) => ch.name !== name) })
  }

  function removeCourse(row) {
    const { course, usedBy, modules } = row
    const parts = []
    if (usedBy.length) parts.push(`${usedBy.map((d) => d.name).join(' and ')} include${usedBy.length === 1 ? 's' : ''} this course — that will be left pointing at nothing.`)
    if (modules.length) parts.push(`${modules.length} module${modules.length === 1 ? '' : 's'} are filed under it. They keep their progress, but with no course to belong to they will reappear here as a course created by accident.`)
    if (!window.confirm(`Delete the ${course.name} course?${parts.length ? `\n\n${parts.join('\n\n')}` : ''}`)) return
    commit(courses.filter((c) => c.id !== course.id))
  }

  /**
   * Turn a course that was created by accident into one somebody meant.
   *
   * A phantom is a course id that only a module's `track` field claims — the
   * page could already NAME the problem but offered no way out of it, so the
   * only fixes were editing the module's course or living with it. Adopting
   * writes the same course a human would have typed, which is why it is the
   * ordinary `courses` list it gets appended to and nothing special afterwards.
   *
   * The chapters its modules already name are carried over, so adopting does
   * not move anything: without them those modules would keep rendering under
   * the trailing "unplanned" heading they render under now.
   */
  function adoptCourse(row) {
    const { course, strayChapters } = row
    if ((courses || []).some((c) => c.id === course.id)) return
    commit([
      ...courses,
      {
        id: course.id,
        name: course.name,
        description: '',
        chapters: (strayChapters || []).map((name, i) => ({
          name,
          color: CHAPTER_COLORS[i % CHAPTER_COLORS.length],
        })),
      },
    ])
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <AdminNav />
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <AdminNav />

      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <Layers className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
          <p className="mt-1 text-sm text-gray-500">
            A course is a reusable bundle of modules that degrees are assembled from. Sharing one
            across degrees is the point: add a module to a course and every degree holding it picks
            the module up, with no edit. Chapters group the modules inside a long course.
          </p>
        </div>
      </div>

      {canEdit && !seeded.courses && (
        <p className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          These are the courses that ship with the app. The first change you make saves them
          properly.
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      {phantoms.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4" />
            {phantoms.length} course{phantoms.length === 1 ? '' : 's'} created by accident
          </p>
          <p className="mt-1 text-amber-800">
            These ids appear on modules but on no course. The app makes a course out of each one, and
            it then shows up on the Degrees page as a real, assignable option. Re-file the module to
            remove it.
          </p>
          <ul className="mt-2 space-y-1">
            {phantoms.map((p) => (
              <li key={p.id}>
                <code className="font-mono text-xs font-semibold">{p.id}</code>
                <span className="text-amber-800">
                  {' '}— from{' '}
                  {p.modules.map((m, i) => (
                    <span key={m.id}>
                      {i > 0 && ', '}
                      <Link to={`/admin/studio/${m.id}`} className="underline hover:no-underline">
                        {m.title || m.id}
                      </Link>
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {rows.map((row) => {
          const { course, isPhantom, modules, published, required, lessons, strayChapters, usedBy, addable } = row
          const chapters = courseChapters(course)
          return (
            <div
              key={course.id}
              className={`rounded-2xl border bg-white p-5 ${isPhantom ? 'border-amber-300' : 'border-gray-200'}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {canEdit && !isPhantom ? (
                      <input
                        type="text"
                        defaultValue={course.name}
                        onBlur={(e) => e.target.value.trim() && e.target.value !== course.name && updateCourse(course.id, { name: e.target.value.trim() })}
                        className="rounded border border-transparent px-1 py-0.5 font-semibold text-gray-900 hover:border-gray-200 focus:border-blue-300 focus:outline-none"
                      />
                    ) : (
                      <h2 className="font-semibold text-gray-900">{course.name}</h2>
                    )}
                    <code className="font-mono text-xs text-gray-400">{course.id}</code>
                    {isPhantom && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        <AlertTriangle className="h-3 w-3" /> Created by accident
                      </span>
                    )}
                    {!isCoursePublished(course) && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                        <EyeOff className="h-3 w-3" /> Hidden from trainees
                      </span>
                    )}
                  </div>
                  {canEdit && !isPhantom ? (
                    <input
                      type="text"
                      defaultValue={course.description}
                      onBlur={(e) => e.target.value !== course.description && updateCourse(course.id, { description: e.target.value })}
                      placeholder="One line describing what this course teaches…"
                      className="mt-1 w-full rounded border border-transparent px-1 py-0.5 text-sm text-gray-600 hover:border-gray-200 focus:border-blue-300 focus:outline-none"
                    />
                  ) : (
                    course.description && <p className="mt-1 max-w-2xl text-sm text-gray-600">{course.description}</p>
                  )}
                </div>
                {canEdit && !isPhantom && (
                  <button
                    onClick={() => updateCourse(course.id, { published: !isCoursePublished(course) })}
                    disabled={saving}
                    title={isCoursePublished(course)
                      ? 'Hide this course from trainees. Nothing is deleted, and the degrees that include it are untouched.'
                      : 'Show this course to trainees again.'}
                    className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  >
                    {isCoursePublished(course)
                      ? <><EyeOff className="h-3 w-3" /> Hide</>
                      : <><Eye className="h-3 w-3" /> Show</>}
                  </button>
                )}
                {canEdit && !isPhantom && (
                  <button
                    onClick={() => removeCourse(row)}
                    disabled={saving}
                    className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                )}
                {canEdit && isPhantom && (
                  <button
                    onClick={() => adoptCourse(row)}
                    disabled={saving}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                  >
                    <Check className="h-3 w-3" /> Make it a real course
                  </button>
                )}
              </div>

              {/* Counts */}
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-gray-400" />
                  <span className="tabular-nums">{published.length}</span> published
                  {modules.length !== published.length && (
                    <span className="inline-flex items-center gap-1 text-gray-400">
                      <EyeOff className="h-3 w-3" />
                      {modules.length - published.length} hidden
                    </span>
                  )}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <GraduationCap className="h-3.5 w-3.5 text-gray-400" />
                  <span className="tabular-nums">{required.length}</span> count towards a degree
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <BookOpen className="h-3.5 w-3.5 text-gray-400" />
                  <span className="tabular-nums">{lessons}</span> lessons in data
                </span>
              </div>

              {/* Modules */}
              <div className="mt-3">
                <span className="text-xs font-medium text-gray-400">Modules</span>
                {modules.length === 0 ? (
                  <p className="mt-1 text-xs text-gray-400">
                    None yet — a course with no modules is harmless, it simply shows nothing until it has some
                  </p>
                ) : (
                  <ol className="mt-1.5 space-y-1">
                    {modules.map((m, i) => (
                      <li
                        key={m.id}
                        className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50 px-2 py-1 text-xs text-gray-700"
                      >
                        {/* The number the trainee sees, which is this position
                            in THIS course — the same module in another course
                            shows a different one. */}
                        <span className="w-12 shrink-0 tabular-nums font-medium text-gray-400">
                          {moduleNumberIn(course, i)}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{m.title}</span>
                        {m.isBuiltIn && (
                          <span
                            title="Defined in src/data/modules.js. It can sit in any number of courses — the course holds the list, so nothing is written to the module."
                            className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                          >
                            in code
                          </span>
                        )}
                        {!isPublished(m) && <EyeOff className="h-3 w-3 shrink-0 text-gray-400" />}
                        {canEdit && !isPhantom && (
                          <span className="flex shrink-0 items-center gap-0.5">
                            <button
                              onClick={() => moveModule(course, m.id, -1)}
                              disabled={saving || i === 0}
                              title="Move up"
                              className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-25"
                            >
                              <ArrowUp className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => moveModule(course, m.id, 1)}
                              disabled={saving || i === modules.length - 1}
                              title="Move down"
                              className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-25"
                            >
                              <ArrowDown className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => removeModuleFromCourse(course, m.id)}
                              disabled={saving}
                              title={`Remove "${m.title}" from ${course.name}. The module is not deleted, and stays in any other course it is in.`}
                              className="rounded p-0.5 text-gray-400 hover:bg-red-100 hover:text-red-600 disabled:opacity-25"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                )}

                {canEdit && !isPhantom && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <select
                      value={draftModule[course.id] || ''}
                      onChange={(e) => setDraftModule((d) => ({ ...d, [course.id]: e.target.value }))}
                      aria-label={`Add a module to ${course.name}`}
                      className="w-72 rounded-lg border border-gray-200 px-2.5 py-1 text-xs focus:border-blue-300 focus:outline-none"
                    >
                      <option value="">Add a module to this course…</option>
                      {addable.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title}{m.isBuiltIn ? ' (in code)' : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => addModuleToCourse(course)}
                      disabled={saving || !draftModule[course.id]}
                      aria-label={`Add a module to ${course.name}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      <Plus className="h-3 w-3" /> Add
                    </button>
                  </div>
                )}
              </div>

              {/* Chapters */}
              <div className="mt-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-xs font-medium text-gray-400">Chapters</span>
                  {chapters.length === 0 && (
                    <span className="text-xs text-gray-400">
                      None — every module in this course shows in one list, which is right until it gets long
                    </span>
                  )}
                  {chapters.map((ch) => (
                    <span
                      key={ch.name}
                      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${CHAPTER_BADGE[ch.color] || CHAPTER_BADGE.blue}`}
                    >
                      {ch.name}
                      {canEdit && (
                        <button
                          onClick={() => removeChapter(course, ch.name)}
                          disabled={saving}
                          title={`Remove the ${ch.name} chapter`}
                          className="ml-0.5 opacity-50 hover:opacity-100 disabled:opacity-30"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>

                {canEdit && !isPhantom && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={draftChapter[course.id] || ''}
                      onChange={(e) => setDraftChapter((d) => ({ ...d, [course.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && addChapter(course)}
                      placeholder="New chapter, e.g. Foundations"
                      className="w-64 rounded-lg border border-gray-200 px-2.5 py-1 text-xs focus:border-blue-300 focus:outline-none"
                    />
                    <button
                      onClick={() => addChapter(course)}
                      disabled={saving || !(draftChapter[course.id] || '').trim()}
                      aria-label={`Add a chapter to ${course.name}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      <Plus className="h-3 w-3" /> Add
                    </button>
                  </div>
                )}

                {strayChapters.length > 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    <AlertTriangle className="mr-1 inline h-3 w-3" />
                    {strayChapters.map((c) => `"${c}"`).join(', ')} {strayChapters.length === 1 ? 'is named by a module but is not a chapter here' : 'are named by modules but are not chapters here'} — those modules show at the end of the course.
                  </p>
                )}
              </div>

              {/* Degrees */}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-xs font-medium text-gray-400">In degrees</span>
                {usedBy.length === 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                    <AlertTriangle className="h-3 w-3" />
                    None — nobody is enrolled in this
                  </span>
                ) : (
                  usedBy.map((d) => (
                    <Link
                      key={d.id}
                      to="/admin/degrees"
                      className="rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-800 hover:bg-purple-100"
                    >
                      {d.name}
                    </Link>
                  ))
                )}
                {usedBy.length > 1 && (
                  <span className="text-xs text-gray-400">
                    — shared, so a new module reaches all {usedBy.length} at once
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {canEdit && (
        <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white p-5">
          <h2 className="text-sm font-semibold text-gray-700">New course</h2>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={draftCourse}
              onChange={(e) => setDraftCourse(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCourse()}
              placeholder="Ad Operations"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-300 focus:outline-none"
            />
            <button
              onClick={addCourse}
              disabled={saving || !draftCourse.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:bg-gray-300"
            >
              <Plus className="h-4 w-4" /> Create
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Then add it to a degree, and file modules into it from AI Studio. A course with no
            modules is harmless — it simply shows nothing until it has some.
          </p>
        </div>
      )}
    </div>
  )
}
